// Two tiny, purely presentational catalog leaf components that are shared by
// BOTH the landing (`home-page.tsx`) and the broader page module (`pages.tsx`,
// e.g. CoursesPage / CoursePage). They live in their own leaf module so the
// landing can render them WITHOUT importing the full `pages.tsx` monolith (and
// its Convex/auth/search/learning-stats graph) - while still keeping a single
// source of truth for the card markup (no duplication). Both render only
// translation-driven anchors; there is no client-only/effect logic here.
import { localizedPath, useTranslation } from '../lib/i18n'
import type { Course, Lesson } from '../content/catalog'
import { ProBadge } from './pro-badge'

export function CourseCard({ course }: { course: Course }) {
  const { locale, t } = useTranslation()
  return (
    <a
      className="course-card"
      href={localizedPath('course', locale, { courseSlug: course.slug })}
    >
      <span>{course.order.toString().padStart(2, '0')}</span>
      <div>
        {/* h2 keeps the courses-index heading hierarchy descending (h1 page
            title -> h2 course cards) without skipping a level; CSS sizes it. */}
        <h2>{course.title}</h2>
        <p>{course.description}</p>
        <div>
          {course.tier === 'pro' ? <ProBadge /> : null}
          <small>
            {course.lessonCount} {t('pages.common.lessonsWord')}
          </small>
          <small>
            {course.estimatedMinutes} {t('pages.common.minutesShort')}
          </small>
        </div>
      </div>
    </a>
  )
}

export function LessonRow({
  lesson,
  displayLabel,
}: {
  lesson: Lesson
  displayLabel?: string
}) {
  const { locale, t } = useTranslation()
  const label = displayLabel ?? lesson.number
  return (
    <a
      className="lesson-row"
      href={localizedPath('lesson', locale, {
        courseSlug: lesson.courseSlug,
        lessonSlug: lesson.slug,
      })}
    >
      <span>{label}</span>
      <div>
        <strong>
          {lesson.title}
          {lesson.access === 'pro' ? <ProBadge className="is-inline" /> : null}
        </strong>
        <p>{lesson.outcome}</p>
      </div>
      <small>
        {lesson.status === 'published'
          ? t('pages.common.available')
          : t('pages.common.roadmap')}
      </small>
    </a>
  )
}
